import { useEffect } from 'react';
import axios from 'axios';
import { API_BASE, STORAGE_BASE } from '../config';

const absUrl = p => {
  if (!p) return '';
  if (/^https?:\/\//.test(p) || p.startsWith('data:')) return p;
  const base = (STORAGE_BASE || '').replace(/\/$/, '');
  return `${base}/${String(p).replace(/^\/+/, '')}`;
};

/** Swap favicon + apple-touch-icon from admin settings (uploads win over build defaults). */
function applyIcons(d) {
  const fav = absUrl(d.favicon || (d.pwa || {}).icon || '');
  if (fav) {
    let link = document.head.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.type = fav.endsWith('.svg') ? 'image/svg+xml' : 'image/png';
    link.href = fav;
  }
  const apple = absUrl((d.pwa || {}).icon || d.favicon || '');
  if (apple) {
    let el = document.head.querySelector('link[rel="apple-touch-icon"]');
    if (!el) {
      el = document.createElement('link');
      el.rel = 'apple-touch-icon';
      document.head.appendChild(el);
    }
    el.href = apple;
  }
}

/** Update-or-create a <meta> tag owned by the site SEO (no duplicates with index.html defaults). */
function upsertMeta(selector, attrs, content) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    el.setAttribute('data-slot', 'site-seo');
    document.head.appendChild(el);
  }
  if (content !== undefined) el.setAttribute('content', content);
  return el;
}

/** Apply global SEO (admin → تنظیمات سایت → سئو) to <title> and meta/og tags. */
function applySiteSeo(d) {
  const seo = d.seo || {};
  const siteName = d.site_name || '';
  const title = seo.title || siteName || (d.pwa || {}).name || '';
  if (title) document.title = title;
  // iOS Safari auto-links phone numbers/dates in blue — disable it site-wide.
  upsertMeta('meta[name="format-detection"]', { name: 'format-detection' }, 'telephone=no,date=no,address=no,email=no');
  if (seo.description) upsertMeta('meta[name="description"]', { name: 'description' }, seo.description);
  if (seo.keywords) upsertMeta('meta[name="keywords"]', { name: 'keywords' }, seo.keywords);
  upsertMeta('meta[name="robots"]', { name: 'robots' }, seo.robots || 'index,follow');
  let canon = document.head.querySelector('link[rel="canonical"]');
  if (seo.canonical) {
    if (!canon) {
      canon = document.createElement('link');
      canon.rel = 'canonical';
      canon.setAttribute('data-slot', 'site-seo');
      document.head.appendChild(canon);
    }
    canon.href = seo.canonical;
  } else if (canon && canon.getAttribute('data-slot') === 'site-seo') {
    canon.remove();
  }
  upsertMeta('meta[property="og:title"]', { property: 'og:title' }, title || document.title);
  if (seo.description) upsertMeta('meta[property="og:description"]', { property: 'og:description' }, seo.description);
  if (siteName) upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, siteName);
  if (seo.og_image) upsertMeta('meta[property="og:image"]', { property: 'og:image' }, seo.og_image);
}

/** Per-page SEO override (product/blog pages). Pass null/{} to fall back to global. */
export function setPageSeo(seo) {
  window.__pageSeo = seo && typeof seo === 'object' ? seo : null;
  window.dispatchEvent(new Event('site-page-seo'));
}

function currentSeo(global) {
  const p = window.__pageSeo;
  if (p && (p.title || p.description)) {
    return {
      seo: { ...(global.seo || {}), ...(p.title ? { title: p.title } : {}), ...(p.description ? { description: p.description } : {}) },
      site_name: global.site_name,
      pwa: global.pwa,
    };
  }
  return global;
}
function injectHtml(target, html, slotId) {
  const attr = `data-slot="${slotId}"`;
  target.querySelectorAll(`[${attr}]`).forEach(el => el.remove());
  if (!html || !html.trim()) return;
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  const frag = tpl.content;
  const mount = (node) => {
    if (node.nodeType !== 1) return null;
    let el;
    if (node.tagName === 'SCRIPT') {
      el = document.createElement('script');
      for (const a of node.attributes) el.setAttribute(a.name, a.value);
      if (node.src) el.src = node.src;
      else el.textContent = node.textContent;
    } else {
      el = node;
    }
    el.setAttribute('data-slot', slotId);
    return el;
  };
  [...frag.childNodes].forEach(node => {
    const el = mount(node);
    if (el) target.appendChild(el);
  });
}

/**
 * Applies server-driven <head> extras on every page:
 * - PWA: dynamic manifest link, theme-color, apple-touch-icon
 * - Admin "head_tags": verification metas (Google console etc.), pixels
 * - Admin "footer_tags": scripts/styles injected at end of <body>
 */
export default function SiteHead() {
  useEffect(() => {
    let alive = true;
    let global = {};
    const apply = () => { try { applySiteSeo(currentSeo(global)); } catch {} };
    const onPageSeo = () => apply();
    window.addEventListener('site-page-seo', onPageSeo);
    axios.get('/site-settings').then(r => {
      if (!alive) return;
      const d = r.data || {};
      global = d;
      apply();
      try { applyIcons(d); } catch {}
      // ---- PWA ----
      const pwa = d.pwa || {};
      if (pwa.enabled) {
        let link = document.querySelector('link[rel="manifest"]');
        if (!link) {
          link = document.createElement('link');
          link.rel = 'manifest';
          document.head.appendChild(link);
        }
        link.href = `${API_BASE}/pwa/manifest.json`;
        let theme = document.querySelector('meta[name="theme-color"]');
        if (!theme) {
          theme = document.createElement('meta');
          theme.name = 'theme-color';
          document.head.appendChild(theme);
        }
        theme.content = pwa.theme_color || '#18181B';
        if (pwa.icon) {
          let apple = document.querySelector('link[rel="apple-touch-icon"]');
          if (!apple) {
            apple = document.createElement('link');
            apple.rel = 'apple-touch-icon';
            document.head.appendChild(apple);
          }
          apple.href = pwa.icon;
        }
      }
      // ---- custom tags ----
      try { injectHtml(document.head, d.head_tags || '', 'admin-head'); } catch {}
      try { injectHtml(document.body, d.footer_tags || '', 'admin-footer'); } catch {}
    }).catch(() => {});
    return () => { alive = false; window.removeEventListener('site-page-seo', onPageSeo); };
  }, []);
  return null;
}
